"use client";

import { useState, useMemo } from "react";
import Image from "next/image";
import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import { FaArrowLeftLong, FaArrowRightLong } from "react-icons/fa6";
import { IoSearch } from "react-icons/io5";

type Destination = {
  id: number;
  title: string;
  page_name: string | null;
  slug: string;
  featured_image: {
    url: string;
  } | null;
  publishedAt: string;
  sort_order: number | null;
};

type DestinationsGridProps = {
  destinations: Destination[];
  locale: string;
};

type SortOption = "newest" | "az" | "za";

const DestinationsGrid = ({ destinations, locale }: DestinationsGridProps) => {
  const t = useTranslations("destinations");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<SortOption>("newest");

  const filteredAndSorted = useMemo(() => {
    let result = [...destinations];

    const displayName = (d: Destination) => d?.page_name || d?.title || "";

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((d) =>
        displayName(d).toLowerCase().includes(query)
      );
    }

    result.sort((a, b) => {
      switch (sortBy) {
        case "newest":
          return (
            new Date(b?.publishedAt).getTime() -
            new Date(a?.publishedAt).getTime()
          );
        case "az":
          return displayName(a).localeCompare(displayName(b), locale);
        case "za":
          return displayName(b).localeCompare(displayName(a), locale);
        default:
          return 0;
      }
    });

    return result;
  }, [destinations, searchQuery, sortBy, locale]);

  if (!destinations?.length) {
    return (
      <div className="my-12 md:my-24">
        <div className="container mx-auto px-5">
          <div className="w-full flex flex-col items-center justify-center mx-auto pt-20">
            <div className="relative h-[100px] w-[100px]">
              <Image
                src="/assets/icons/empty.png"
                alt="no destinations icon"
                fill
                className="object-cover"
              />
            </div>
            <p className="text-[#333] text-2xl pt-5">
              {locale === "en"
                ? "No destinations found. Please check back later."
                : "لم يتم العثور على وجهات. يرجى المراجعة لاحقاً."}
            </p>
            <Link
              href="/"
              locale={locale}
              className="flex items-center gap-2 bg-[#5a558e] text-white py-3 px-6 rounded-md shadow-sm mt-4 hover:bg-[#5a558e]/80 transition-all duration-300 ease-linear"
            >
              {locale === "en" ? <FaArrowLeftLong /> : <FaArrowRightLong />}
              {locale === "en" ? "Back to Home" : "العودة للرئيسية"}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="my-12 md:my-24">
      <div className="container mx-auto px-5">
        <div className="flex flex-col sm:flex-row items-center gap-4 mb-8">
          <div className="relative w-full sm:flex-1">
            <IoSearch className="absolute top-1/2 -translate-y-1/2 start-4 text-gray-400 text-lg" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("searchPlaceholder")}
              className="w-full ps-11 pe-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#5a558e] transition-colors"
            />
          </div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            className="w-full sm:w-auto px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#5a558e] bg-white transition-colors cursor-pointer"
          >
            <option value="newest">{t("sortNewest")}</option>
            <option value="az">{t("sortAZ")}</option>
            <option value="za">{t("sortZA")}</option>
          </select>
        </div>

        {filteredAndSorted.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="relative h-[80px] w-[80px] mb-4">
              <Image
                src="/assets/icons/empty.png"
                alt="no results icon"
                fill
                className="object-cover"
              />
            </div>
            <p className="text-[#333] text-xl">{t("noResults")}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAndSorted.map((destination, index) => {
              const imageUrl = destination?.featured_image?.url;
              const title = destination?.page_name || destination?.title;
              const slug = destination?.slug;

              return (
                <Link
                  href={`/destinations/${slug}`}
                  key={destination?.id || index}
                  locale={locale}
                  className="group relative block h-[280px] rounded-xl overflow-hidden shadow-md"
                >
                  {imageUrl && (
                    <Image
                      src={imageUrl}
                      alt={title || "Destination"}
                      fill
                      className="object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h3 className="text-xl font-bold text-white">{title}</h3>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DestinationsGrid;
